import { useCallback, useEffect, useState } from 'react';
import {
  fetchLeaveSummary, fetchMyLeave, fetchLeaveApprovals, fetchTeamAway,
  requestLeave, decideLeave, cancelLeave, LEAVE_KINDS,
  type LeaveSummary, type LeaveRow, type ApprovalRow, type AwayRow,
} from '../lib/leave';
import { onFail } from '../lib/errors';
import './leave.css';

/* =====================================================================================
   Leave: book it, approve it, see who is away.

   Requests route the way expenses do: to the line manager from Crew, the board to the
   owner. Balances count annual leave only; sickness and the rest are recorded, not
   deducted. The team view shows WHEN people are away and keeps WHY between the person,
   the owner and HR, which is why sickness shows to colleagues as simply "away".
   ===================================================================================== */

const fmtD = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('en-GB',
  { day: 'numeric', month: 'short' });
const kindLabel = (k: string) => LEAVE_KINDS.find(([key]) => key === k)?.[1]
  ?? (k === 'leave' ? 'Leave' : k === 'away' ? 'Away' : k);
const STATUS_PILL: Record<string, string> = {
  requested: 'info', approved: 'ok', refused: 'warn', cancelled: 'mute',
};

export function LeaveView() {
  const [summary, setSummary] = useState<LeaveSummary | null>(null);
  const [mine, setMine] = useState<LeaveRow[]>([]);
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [away, setAway] = useState<AwayRow[]>([]);
  const [requesting, setRequesting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchLeaveSummary().then(setSummary).catch(() => setSummary(null));
    fetchMyLeave().then(setMine).catch(onFail('Your leave', () => setMine([])));
    fetchLeaveApprovals().then(setApprovals).catch(() => setApprovals([]));
    fetchTeamAway().then(setAway).catch(() => setAway([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="page leave">
      <div className="page-head">
        <h1 className="page-title">Leave</h1>
        <p className="page-sub">
          Book leave, record sickness, see who is away. Requests go to your line manager;
          colleagues see when you are away, never why.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {approvals.length > 0 && (
        <section className="card lv-approvals">
          <h2>Awaiting your decision</h2>
          {approvals.map((a) => (
            <div key={a.out_id} className="lv-approval">
              <div>
                <strong>{a.out_who}</strong> · {kindLabel(a.out_kind)} ·{' '}
                {fmtD(a.out_starts)} to {fmtD(a.out_ends)} ({a.out_days} working day{a.out_days === 1 ? '' : 's'})
                {a.out_fit_note && <span className="pill warn">fit note expected</span>}
                {a.out_reason && <div className="cell-sub">{a.out_reason}</div>}
              </div>
              <div className="lv-actions">
                <button className="btn primary" onClick={() =>
                  decideLeave(a.out_id, true).then(() => { setMsg('Approved.'); load(); })
                    .catch(onFail('The decision'))}>Approve</button>
                <button className="btn" onClick={() => {
                  const note = window.prompt('Say why, so the refusal is usable:');
                  if (note === null) return;
                  decideLeave(a.out_id, false, note).then(() => { setMsg('Refused, with your note.'); load(); })
                    .catch(onFail('The decision'));
                }}>Refuse</button>
              </div>
            </div>
          ))}
        </section>
      )}

      {summary && (
        <div className="metrics">
          <div className="metric card"><div className="label">Allowance</div>
            <div className="metric-value">{summary.out_allowance}</div>
            <div className="metric-foot"><span>days, year from {fmtD(summary.out_year_start)}
              {Number(summary.out_adjustments) !== 0 && <> · includes {summary.out_adjustments > 0 ? '+' : ''}{summary.out_adjustments} adjusted</>}</span></div></div>
          <div className="metric card"><div className="label">Taken</div>
            <div className="metric-value">{summary.out_taken}</div></div>
          <div className="metric card"><div className="label">Booked ahead</div>
            <div className="metric-value">{summary.out_booked}</div>
            {summary.out_pending > 0 && <div className="metric-foot"><span>+{summary.out_pending} awaiting approval</span></div>}</div>
          <div className="metric card"><div className="label">Remaining</div>
            <div className="metric-value">{summary.out_remaining}</div>
            <div className="metric-foot"><span>annual leave only; sickness never deducts</span></div></div>
        </div>
      )}

      <div className="toolbar">
        <button className="btn primary" onClick={() => setRequesting(true)}>Request leave</button>
      </div>

      <section className="card">
        <h2 className="lv-h">Your requests</h2>
        {mine.length === 0 && <p className="module-empty">Nothing yet. The balance above is waiting.</p>}
        {mine.map((r) => (
          <div key={r.out_id} className="lv-row">
            <div>
              <strong>{kindLabel(r.out_kind)}</strong> · {fmtD(r.out_starts)} to {fmtD(r.out_ends)}
              {' '}· {r.out_days} day{r.out_days === 1 ? '' : 's'}
              <span className={`pill ${STATUS_PILL[r.out_status] ?? 'mute'}`}>{r.out_status}</span>
              {r.out_fit_note && r.out_status !== 'refused' && <span className="pill warn">fit note expected</span>}
              {r.out_rtw_done && <span className="pill ok">return-to-work done</span>}
              {r.out_decision_note && <div className="cell-sub">{r.out_decision_note}</div>}
            </div>
            {(r.out_status === 'requested'
              || (r.out_status === 'approved' && r.out_starts > new Date().toISOString().slice(0, 10))) && (
              <button className="link-btn" onClick={() =>
                cancelLeave(r.out_id).then(() => { setMsg('Cancelled.'); load(); })
                  .catch(onFail('The cancellation'))}>Cancel</button>
            )}
          </div>
        ))}
      </section>

      <section className="card">
        <h2 className="lv-h">Who is away, next 60 days</h2>
        {away.length === 0 && <p className="module-empty">Nobody. A suspiciously dedicated team.</p>}
        {away.map((a, i) => (
          <div key={i} className="lv-row">
            <div><strong>{a.out_who}</strong> · {fmtD(a.out_starts)} to {fmtD(a.out_ends)}
              <span className="pill mute">{kindLabel(a.out_kind)}</span></div>
          </div>
        ))}
      </section>

      {requesting && (
        <RequestForm onClose={() => setRequesting(false)}
                     onDone={() => { setRequesting(false); setMsg('Requested. Your approver has been told.'); load(); }} />
      )}
    </div>
  );
}

function RequestForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ kind: 'annual', start: today, end: today,
    startHalf: false, endHalf: false, reason: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer lv-drawer">
        <h2>Request leave</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Kind</span>
          <select className="input sel" value={f.kind} onChange={set('kind')}>
            {LEAVE_KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        {f.kind === 'sick' && (
          <p className="muted lv-note">
            Sickness is visible only to you, the owner and HR; colleagues see "away". If it
            follows an injury at work, report the incident in Safety too and the two records
            are joined there.
          </p>
        )}
        <div className="lv-two">
          <label className="cf-field"><span>First day</span>
            <input className="input" type="date" value={f.start} onChange={set('start')} /></label>
          <label className="cf-field"><span>Last day</span>
            <input className="input" type="date" value={f.end} onChange={set('end')} /></label>
        </div>
        <div className="lv-two">
          <label className="lv-check"><input type="checkbox" checked={f.startHalf}
            onChange={(e) => setF({ ...f, startHalf: e.target.checked })} />
            <span>First day is a half day</span></label>
          <label className="lv-check"><input type="checkbox" checked={f.endHalf}
            onChange={(e) => setF({ ...f, endHalf: e.target.checked })} />
            <span>Last day is a half day</span></label>
        </div>
        <label className="cf-field"><span>Note for your approver (optional)</span>
          <input className="input" value={f.reason} onChange={set('reason')} /></label>
        <div className="exp-decide-actions">
          <button className="btn primary" disabled={busy} onClick={async () => {
            setBusy(true); setErr(null);
            try { await requestLeave(f); onDone(); }
            catch (e) { setErr(String((e as Error).message)); setBusy(false); }
          }}>Request</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}
